import React, { useState, useEffect } from 'react';
import { Link, useHistory, useLocation } from "react-router-dom";
import api from '../api'; 

const Header = () => {
  const history = useHistory();
  const location = useLocation();

  const [showDropdown, setShowDropdown] = useState(false);
  const [userName, setUserName] = useState("User");
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [cartCount, setCartCount] = useState(0);

  // Wishlist & Search States
  const [wishlistCount, setWishlistCount] = useState(0);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [keyword, setKeyword] = useState("");

  // MOBILE MENU STATE
  const [menuOpen, setMenuOpen] = useState(false);

  /* ================= HELPERS ================= */
  const updateCartCount = async () => {
    const userId = localStorage.getItem("userId");
    if (!userId || userId === "0") {
      setCartCount(0);
      return;
    }
    try {
      const response = await api.get(`/usercart/cartcount?userId=${userId}`);
      const count = Number(response.data);
      setCartCount(isNaN(count) ? 0 : count);
    } catch (error) {
      console.error("Error fetching cart count:", error);
      setCartCount(0);
    }
  };

  const updateWishlistCount = () => {
    const stored = localStorage.getItem("wishlist");
    if (stored) {
      const parsed = JSON.parse(stored);
      setWishlistCount(parsed.length);
    } else {
      setWishlistCount(0);
    }
  };

  const getNavStyle = (path) => {
    const isActive = location.pathname === path;
    return {
      color: isActive ? '#FFC35B' : '#333', 
      fontWeight: isActive ? '600' : '400',
      borderBottom: isActive ? '2px solid #FFC35B' : '2px solid transparent',
      textShadow: isActive ? '0px 0px 12px rgba(255, 195, 91, 0.6)' : 'none', 
      paddingBottom: '5px',
      textDecoration: 'none',
      transition: 'all 0.3s ease'
    };
  };

  /* ================= EFFECTS ================= */
  useEffect(() => {
    setMenuOpen(false); 
    
    const loggedInStatus = localStorage.getItem("isLoggedIn") === "true";
    setIsLoggedIn(loggedInStatus);

    if (loggedInStatus) {
      const userStr = localStorage.getItem("user");
      if (userStr) {
        try {
          const userObj = JSON.parse(userStr);
          setUserName(userObj.parName || "User");
        } catch {
          setUserName("User");
        }
      }
      updateCartCount();
    } else {
      setCartCount(0);
    }

    updateWishlistCount();

    window.addEventListener("wishlistUpdated", updateWishlistCount);
    window.addEventListener("storage", updateWishlistCount);

    return () => {
      window.removeEventListener("wishlistUpdated", updateWishlistCount);
      window.removeEventListener("storage", updateWishlistCount);
    };
  }, [location.pathname]);

  /* ================= HANDLERS ================= */
  const handleLogout = () => {
    localStorage.clear();
    setShowDropdown(false);
    setIsLoggedIn(false);
    setCartCount(0);
    setWishlistCount(0);
    history.push("/");
    window.location.reload();
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    if (keyword.trim()) {
      history.push(`/search?q=${keyword}`);
      setIsSearchOpen(false);
    }
  };

  const toggleSearch = () => {
    setIsSearchOpen(!isSearchOpen);
    if (!isSearchOpen) {
      setTimeout(() => document.getElementById("searchInput")?.focus(), 100);
    }
  };

  return (
    <header className="app-header-bar">
      {/* 1. LOGO */}
      <Link to="/" className="logo-container" style={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
        <img
          className="first"
          src="/assets/images/header_logo.png"
          alt="Divine Depot Logo"
          style={{ 
            height: '50px',       
            width: 'auto',        
            maxWidth: '160px',    
            objectFit: 'contain'  
          }} 
        />
      </Link>

      {/* 2. NAV MENU */}
      <ul className={`nav-links ${menuOpen ? "active" : ""}`}>
        <li><Link to="/" style={getNavStyle("/")}>Home</Link></li>
        <li><Link to="/about" style={getNavStyle("/about")}>About Us</Link></li>
        <li><Link to="/our-shop" style={getNavStyle("/our-shop")}>Our Shop</Link></li>
        <li><Link to="/contact-us" style={getNavStyle("/contact-us")}>Contact Us</Link></li>
        
        <li className="mobile-only close-menu" onClick={() => setMenuOpen(false)}>
            <span>Close Menu ×</span>
        </li>
      </ul>

      {/* 3. RIGHT ICONS CONTAINER */}
      <div className="right-icons" style={{ display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
        
        {/* SEARCH */}
        <div style={searchContainerStyle(isSearchOpen)}>
          {isSearchOpen ? (
            <form onSubmit={handleSearchSubmit} style={{ display: 'flex', width: '100%' }}>
              <input
                id="searchInput"
                type="text"
                placeholder="Search..."
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                onBlur={() => !keyword && setIsSearchOpen(false)}
                style={searchInputStyle}
              />
              <button type="submit" style={{ background: 'none', border: 'none', cursor: 'pointer' }}>→</button>
              <span onClick={() => setIsSearchOpen(false)} style={{ cursor: 'pointer', marginLeft: '5px' }}>×</span>
            </form>
          ) : (
            <img
              src="/assets/images/iconoir_search.png"
              alt="Search"
              onClick={toggleSearch}
              style={{ cursor: 'pointer', width: '22px' }}
            />
          )}
        </div>

        {/* ICONS GROUP (Wishlist & Cart) */}
        <div className="menu1" style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
          
          {/* Wishlist */}
          <Link to="/wishlist" style={{ position: 'relative', display: 'flex' }}>
            <img src="/assets/images/heart-svgrepo-com (1).svg" alt="Wishlist" style={{ width: '22px', height: '22px' }} />
            {wishlistCount > 0 && <span style={badgeStyle}>{wishlistCount}</span>}
          </Link>

          {/* Cart */}
          <Link to="/cart" style={{ position: 'relative', display: 'flex' }}>
            <img src="/assets/images/Vector.png" alt="Cart" width="22" />
            {cartCount > 0 && <span style={badgeStyle}>{cartCount}</span>}
          </Link>
        </div>

        {/* USER PROFILE */}
        {isLoggedIn ? (
          <div style={{ position: 'relative' }}>
            <div onClick={() => setShowDropdown(!showDropdown)} style={{ cursor: 'pointer' }}>
              <div style={avatarStyle}>
                {userName.charAt(0).toUpperCase()}
              </div>
            </div>
            {showDropdown && (
              <div style={dropdownStyle}>
                <p><strong>Hello, {userName}</strong></p>
                <Link to="/profile" style={dropdownLinkStyle}>My Profile</Link>
                <Link to="/orders" style={dropdownLinkStyle}>Order History</Link>
                <Link to="/messages" style={dropdownLinkStyle}>My Messages</Link>
                <Link to="/cart" style={dropdownLinkStyle}>My Cart</Link>
                <button onClick={handleLogout} style={logoutBtn}>Logout</button>
              </div>
            )}
          </div>
        ) : (
          /* ➤ UPDATED: loginButtonStyle now uses Flexbox for perfect centering */
          <button style={loginButtonStyle} onClick={() => history.push("/login")}>
            Log in
          </button>
        )}

        {/* 4. HAMBURGER ICON */}
        <div className="hamburger" onClick={() => setMenuOpen(!menuOpen)} style={{ marginLeft: '5px' }}>
           <div className="bar"></div>
           <div className="bar"></div>
           <div className="bar"></div>
        </div>

      </div>
    </header>
  );
};

/* ================= STYLES ================= */

const searchContainerStyle = (isOpen) => ({
  display: 'flex',
  alignItems: 'center',
  width: isOpen ? '140px' : '25px', 
  transition: '0.3s',
  overflow: 'hidden'
});

const searchInputStyle = { border: 'none', outline: 'none', width: '100%', background: 'transparent', fontSize: '13px' };
const badgeStyle = { position: 'absolute', top: '-8px', right: '-8px', background: '#dc3545', color: '#fff', borderRadius: '50%', width: '16px', height: '16px', fontSize: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center' };
const avatarStyle = { width: '35px', height: '35px', background: '#FFC35B', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 'bold', fontSize: '14px' };
const dropdownStyle = { position: 'absolute', top: '50px', right: 0, background: '#fff', padding: '15px', borderRadius: '10px', boxShadow: '0 4px 12px rgba(0,0,0,0.1)', zIndex: 1000, minWidth: '150px' };
const dropdownLinkStyle = { display: 'block', padding: '8px 0', textDecoration: 'none', color: '#333' };
const logoutBtn = { background: 'none', border: 'none', color: '#dc3545', cursor: 'pointer', fontWeight: 'bold', marginTop: '10px' };

// ➤ FIXED: Perfect Centering with Flexbox
const loginButtonStyle = {
    backgroundColor: '#FFC35B',
    color: '#222',
    border: 'none',
    padding: '0 12px',     // No top/bottom padding needed with flexbox + fixed height
    borderRadius: '20px', 
    fontWeight: '700',
    fontSize: '12px',      
    cursor: 'pointer',
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)', 
    transition: 'transform 0.2s',
    whiteSpace: 'nowrap',
    minWidth: '60px',      
    height: '30px',        // Fixed Height
    
    // Flexbox Centering Magic
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
};

export default Header;